import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, HardDrive, Building2, Waves, LogOut, Server, Rocket, Shield, Users, ScrollText, Network, BookOpen } from "lucide-react";
import ProfileModal from "@/components/ProfileModal";

const nav = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, superOnly: false },
  { to: "/onboarding", label: "Onboarding", icon: Rocket, end: false, superOnly: true },
  { to: "/servers", label: "Serveurs SIP", icon: HardDrive, end: false, superOnly: true },
  { to: "/mt-3cx", label: "3CX Multi-tenant", icon: Network, end: false, superOnly: true },
  { to: "/logs", label: "Journaux", icon: ScrollText, end: false, superOnly: true },
  { to: "/hotels", label: "Hôtels", icon: Building2, end: false, superOnly: false },
  { to: "/guide", label: "Guide 3CX", icon: BookOpen, end: false, superOnly: true },
  { to: "/team", label: "Mon équipe", icon: Users, end: false, superOnly: false, dsiOnly: true },
  { to: "/deploy", label: "Déploiement", icon: Server, end: false, superOnly: true },
  { to: "/admins", label: "Admins", icon: Shield, end: false, superOnly: true },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSuper = user?.role === "super_admin";
  const isDsi = user?.role === "hotel_admin";
  const [profileOpen, setProfileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 flex w-60 flex-col bg-zinc-950 text-zinc-300">
        <div className="flex items-center gap-2 px-5 py-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-blue-600">
            <Waves className="h-4 w-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-head text-base font-black tracking-tighter text-white">
            HotelSIP<span className="text-blue-500">.</span>
          </span>
        </div>

        <nav className="mt-4 flex-1 space-y-0.5 px-3">
          {nav
            .filter((n) => {
              if (n.superOnly && !isSuper) return false;
              if (n.dsiOnly && !isDsi) return false;
              return true;
            })
            .map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? "bg-zinc-800 text-white"
                      : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100"
                  }`
                }
              >
                <n.icon className="h-4 w-4" />
                {n.label}
              </NavLink>
            ))}
        </nav>

        <div className="border-t border-zinc-800 p-3">
          <button
            onClick={() => setProfileOpen(true)}
            className="w-full px-2 pb-2 text-left hover:opacity-80 transition-opacity"
          >
            <div className="truncate text-sm font-medium text-zinc-200">{user?.name}</div>
            <div className="font-mono text-[11px] text-zinc-500">
              {isSuper ? "super_admin" : user?.role === "hotel_staff" ? "hotel_staff" : "hotel_admin"}
            </div>
          </button>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 rounded-sm px-2 py-2 text-sm text-zinc-400 transition-colors hover:bg-zinc-900 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" />
            Déconnexion
          </button>
          <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
        </div>
      </aside>

      {/* Content */}
      <main className="ml-60 flex-1">
        <div className="mx-auto max-w-7xl px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
